import { makeStyles } from '@material-ui/styles'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { logoutUrl } from 'app/constants'
import store, { RootState } from 'app/store'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { MessageTypes } from 'core/components/notifications/model'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import { useToast } from 'core/providers/ToastProvider'
import { SessionState, sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { SESSION_EXPIRY_WARNING_THRESHOLD } from 'core/watchdog/session-timeout'
import { noop } from 'utils/fp'

// Helper function to format time in minutes and seconds
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * @description
 * Modal component that warns users when their session is about to expire.
 * Shows 2 minutes before expiry with options to extend session or sign out.
 * @returns {JSX.Element} - The SessionExpiryModal component.
 */
const SessionExpiryModal: React.FC = () => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const dispatch = useDispatch()
  const showToast = useToast()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { showSessionExpiryWarning } = session

  const [countdown, setCountdown] = useState<number>(SESSION_EXPIRY_WARNING_THRESHOLD)

  // Ref to store interval
  const intervalRef = useRef<NodeJS.Timeout>()

  // Update countdown every second
  useEffect(() => {
    if (showSessionExpiryWarning) {
      intervalRef.current && clearInterval(intervalRef.current)
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          // Auto sign out when countdown reaches 0
          if (prev <= 1) {
            handleSignOut()
            showToast('Your session has expired, please log in again', MessageTypes.warning, 5000, {
              vertical: 'top',
              horizontal: 'center',
            })
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      intervalRef.current && clearInterval(intervalRef.current)
    }
  }, [showSessionExpiryWarning, intervalRef])

  // Modal Action handlers
  const dismissSessionExpiryWarning = useCallback(() => {
    dispatch(
      sessionActions.updateSession({
        showSessionExpiryWarning: false,
        sessionExpiryWarningDismissed: true,
      }),
    )
  }, [dispatch])

  const handleSignOut = useCallback(() => {
    dismissSessionExpiryWarning()
    history.push(logoutUrl) //This is an intentional logout so we are redirecting to the logout URL
  }, [dismissSessionExpiryWarning, history])

  // If session expiry warning is not shown, do not render anything
  if (!showSessionExpiryWarning) {
    return null
  }

  return (
    <Modal
      open
      panel="dialog"
      onClose={dismissSessionExpiryWarning}
      onBackdropClick={noop}
      title={
        <div className={classes.title}>
          <FontAwesomeIcon className={classes.icon} size="lg" solid>
            exclamation-triangle
          </FontAwesomeIcon>
          <Text variant="h4">Session Expiring Soon</Text>
        </div>
      }
      maxWidth={500}
      showOverlay={true}
      footer={
        <div className={classes.footer}>
          <Button variant="secondary" onClick={dismissSessionExpiryWarning}>
            Dismiss
          </Button>
          <Button variant="primary" onClick={handleSignOut}>
            Re-login
          </Button>
        </div>
      }
    >
      <div className={classes.content}>
        <Text variant="body2" component="div">
          Your session is about to expire. Please click Re-login to sign in again and continue your
          work.
        </Text>
        <Text variant="body2" component="div">
          If you dismiss this message, your session will automatically log out in
          <strong> {formatTime(countdown)}</strong> minutes.
        </Text>
      </div>
    </Modal>
  )
}

export default SessionExpiryModal

const useStyles = makeStyles<Theme>((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  icon: {
    color: theme.palette.warning.main,
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  note: {
    fontSize: 12,
  },
}))
